/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { NotAvailable } from '@/components/blocks/NotAvailable';
import { CatalogEntryListFacetChips } from '@/components/content/CatalogEntryList/parts/FacetChips';
import { CatalogEntryListProductGrid } from '@/components/content/CatalogEntryList/parts/ProductGrid';
import { CatalogEntryListResultsHeader } from '@/components/content/CatalogEntryList/parts/ResultsHeader';
import { catalogEntryListContainerSX } from '@/components/content/CatalogEntryList/styles/container';
import { useCatalogEntryList } from '@/data/Content/CatalogEntryList';
import { useLocalization } from '@/data/Localization';
import { ITEM_LIST_IDS } from '@/data/constants/gtm';
import { ContentProvider } from '@/data/context/content';
import { ID } from '@/data/types/Basic';
import { GTMContainerListType } from '@/data/types/GTM';
import { Stack, Typography } from '@mui/material';
import { FC, useEffect, useMemo } from 'react';

export const CatalogEntryList: FC<{ id: ID }> = ({ id }) => {
	const catalogEntryList = useCatalogEntryList(id);
	const { entitled, loading, filteredParams, onTriggerMarketingEvent, total = 0, collectionPageData, catGrpDetails } = catalogEntryList;
	const { searchTerm } = filteredParams;
	const { notAvailable } = useLocalization('Category');
	const ProductGridNLS = useLocalization('ProductGrid').Labels;
	const listData = useMemo(
		() =>
			!searchTerm
				? ({
						productListData: { listId: ITEM_LIST_IDS.ITEM_LIST, listName: ProductGridNLS.gtmItemList.t() },
				  } as GTMContainerListType)
				: undefined,
		[ProductGridNLS.gtmItemList, searchTerm]
	);

	const contextValue = useMemo(
		() => ({ ...catalogEntryList, ...listData }),
		[catalogEntryList, listData]
	);
	useEffect(() => {
		if (searchTerm) {
			onTriggerMarketingEvent(searchTerm);
		}
	}, [searchTerm, onTriggerMarketingEvent]); 
	
	return !loading && !entitled && !searchTerm ? ( 
		<NotAvailable message={notAvailable.t()} />
	) : (
		<ContentProvider value={contextValue}>
			{searchTerm && 
				<Typography variant="h2" >
					{ProductGridNLS.SearchProductsCount.t({searchTerm:searchTerm,count:catalogEntryList?.total?String(catalogEntryList?.total):'0'})}
				</Typography>
			}
			<Typography component='span' className='category-name' display={'none'}>{catGrpDetails?.at(0)?.name}</Typography>
			<Stack
				spacing={2}
				sx={catalogEntryListContainerSX}
				id="catalog-entry-list-product-container"
				data-testid="catalog-entry-list-product-container"
			>
				<CatalogEntryListResultsHeader total={total} />
				<CatalogEntryListFacetChips />
				<CatalogEntryListProductGrid  total={total} />
				<CatalogEntryListResultsHeader total={total} />
			</Stack>
			{/* {collectionPageData ? (
				<script id="collectionPage" type="application/ld+json" dangerouslySetInnerHTML={{ __html: collectionPageData  }}></script>
				) 
			: null} */}
		</ContentProvider>
	);
};